import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { TokenStatus, SessionData } from "./agentSlice";
import { logout } from "../../Store_/Slices/AuthSlice";

const BASE_URL =
  process.env.REACT_APP_API_URL_DEV || "http://localhost:4002/api";

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: "include",
  });

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshResult = await baseQuery(
      {
        url: "/user/refresh-token",
        method: "POST",
      },
      api,
      extraOptions
    );

    if (refreshResult.data) {
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

export interface CreateSessionResponse {
  data: SessionData;
}

export interface TokenStatusResponse {
  tokens: TokenStatus;
}

export interface CreateSessionRequest {
  /** The browser's SDP offer. The backend relays it to /v1/realtime/calls. */
  sdp: string;
  venueId?: string;
}

export interface TrackTokensRequest {
  tokensUsed: number;
}

export interface SendTextMessageRequest {
  message: string;
  sessionId: string;
}

export interface SendTextMessageResponse {
  reply: string;
  functionCalls?: Array<{
    id: string;
    name: string;
    arguments: string;
  }>;
  tokensUsed: number;
  sessionId: string;
}

export interface CreateVenueRequest {
  name: string;
  totalSpaces: number;
  type: "vibe" | "occassion" | "architecture";
  address: string;
  status?: "draft" | "published";
}

export interface UpdateVenueRequest {
  id: string;
  name?: string;
  totalSpaces?: number;
  type?: "vibe" | "occassion" | "architecture";
  address?: string;
  status?: "draft" | "published";
}

export interface VenueResponse {
  type: "success";
  message: string;
  data: any;
}

export interface SearchSpaceParams {
  name?: string;
  address?: string;
  minRate?: number;
  maxRate?: number;
  minCapacity?: number;
  maxCapacity?: number;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  tags?: string;
  features?: string;
  page?: number;
  limit?: number;
}

export interface SearchSpaceResponse {
  type: "success";
  data: Array<{
    id: string;
    name: string;
    address: string;
    ratePerHour: number;
    capacity: number;
    status: string;
    user: {
      id: string;
      name: string;
      image: string;
    };
    media: Array<{
      id: string;
      url: string;
      type: string;
      number: number;
    }>;
    availabilities: Array<{
      id: string;
      day: string;
      startTime: string;
      endTime: string;
    }>;
    tags: Array<{
      id: string;
      type: string;
      text: string;
    }>;
    features: Array<{
      id: string;
      type: string;
      text: string;
    }>;
    days: string[];
  }>;
  pagination: {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    totalPages: number;
    nextPage: number | null;
  };
}

export const agentApi = createApi({
  reducerPath: "agentApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Session", "Tokens", "Venue", "Space"],
  endpoints: (builder) => ({
    // Session endpoints
    editVenueSession: builder.mutation<CreateSessionResponse, CreateSessionRequest>({
      query: (data) => ({
        url: "/agent/edit-venue-session",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Session"],
    }),
    searchSpaceSession: builder.mutation<CreateSessionResponse, CreateSessionRequest>({
      query: (data) => ({
        url: "/agent/search-space-session",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Session"],
    }),

    // Text message endpoint
    sendTextMessage: builder.mutation<
      SendTextMessageResponse,
      SendTextMessageRequest
    >({
      query: (data) => ({
        url: "/agent/send-text-message",
        method: "POST",
        body: data,
      }),
    }),

    // Venue endpoints
    createVenue: builder.mutation<VenueResponse, CreateVenueRequest>({
      query: (data) => ({
        url: "/venue/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Venue"],
    }),
    updateVenue: builder.mutation<VenueResponse, UpdateVenueRequest>({
      query: (data) => ({
        url: `/venue/update`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Venue"],
    }),

    searchSpace: builder.query<SearchSpaceResponse, SearchSpaceParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            queryParams.append(key, String(value));
          }
        });
        return `/agent/search-spaces?${queryParams.toString()}`;
      },
      providesTags: ["Space"],
    }),

    trackTokens: builder.mutation<{ success: boolean }, TrackTokensRequest>({
      query: (data) => ({
        url: "/agent/track-tokens",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Tokens"],
    }),
    getTokenStatus: builder.query<TokenStatusResponse, void>({
      query: () => "/agent/token-status",
      providesTags: ["Tokens"],
    }),
  }),
});

export const {
  useEditVenueSessionMutation,
  useSearchSpaceSessionMutation,
  useSendTextMessageMutation,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useSearchSpaceQuery,
  useLazySearchSpaceQuery,
  useTrackTokensMutation,
  useGetTokenStatusQuery,
  useLazyGetTokenStatusQuery,
} = agentApi;
